import React, { useState } from "react";
import { Modal, Form, Radio, Select, Input, Alert, Spin, message } from "antd";
import { uldService } from "../../../../api/enhancedApi";
import { ULD_CORE_STATUS } from "../constants/uldConstantsV3";
import { ULD_LOCATION_TYPE } from "../constants/uldConstants";
import { useUldAirports } from "../../hooks/useUldAirports";

const { TextArea } = Input;
const { Option } = Select;

/**
 * 丢失ULD找回弹窗。
 * 找回必须同一次提交：去向（完好回在库 / 损坏进停用）、原因、在库类地点
 * （机场或不带运输趟次的其他地点）。航班和带趟次的地点不开放选择——
 * 在飞机或卡车上找到的板，等落到机场再找回，或走报文强制通过。
 */
const UldRecoverModal = ({ visible, onCancel, onSuccess, record }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [locationType, setLocationType] = useState(ULD_LOCATION_TYPE.AIRPORT);
    const { airportsList, loading: airportsLoading } = useUldAirports();

    const uldId = record?.master?.id;
    const uldIdentifier = record?.master?.uld_identifier || "";

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            const payload = {
                new_core_status: values.target_status,
                remarks: values.reason,
                recovery_location_type: locationType,
            };
            if (locationType === ULD_LOCATION_TYPE.AIRPORT) {
                payload.recovery_airport_iata = values.airport_iata;
            } else {
                payload.recovery_other_location = values.other_location;
            }

            const response = await uldService.updateSingleUldCoreStatus(uldId, payload);
            if (response.data?.success) {
                message.success("找回成功");
                form.resetFields();
                setLocationType(ULD_LOCATION_TYPE.AIRPORT);
                onSuccess?.();
            } else {
                message.error(response.data?.message || "找回失败");
            }
        } catch (error) {
            if (error?.errorFields) {
                return; // 表单校验失败，antd 已就地提示
            }
            console.error("找回失败:", error);
            message.error(error.response?.data?.detail || "找回失败");
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setLocationType(ULD_LOCATION_TYPE.AIRPORT);
        onCancel?.();
    };

    return (
        <Modal
            title={`找回ULD ${uldIdentifier}`}
            open={visible}
            onOk={handleSubmit}
            onCancel={handleCancel}
            okText="确认找回"
            cancelText="取消"
            confirmLoading={loading}
            destroyOnHidden
        >
            <Alert
                type="info"
                showIcon
                message="找回只能确认在库类地点：机场，或不带运输趟次的其他地点。若板还在飞机或卡车上，等到站后再找回，或走报文强制通过。"
                style={{ marginBottom: 16 }}
            />
            <Form
                form={form}
                layout="vertical"
                preserve={false}
                initialValues={{ target_status: ULD_CORE_STATUS.IN_STOCK }}
            >
                <Form.Item
                    name="target_status"
                    label="找回去向"
                    rules={[{ required: true, message: "请选择找回去向" }]}
                >
                    <Radio.Group>
                        <Radio value={ULD_CORE_STATUS.IN_STOCK}>完好，回到在库</Radio>
                        <Radio value={ULD_CORE_STATUS.OUT_OF_SERVICE}>损坏，进入停用</Radio>
                    </Radio.Group>
                </Form.Item>

                <Form.Item label="找回位置（在库类地点）" required style={{ marginBottom: 8 }}>
                    <Radio.Group
                        value={locationType}
                        onChange={(e) => setLocationType(e.target.value)}
                        style={{ marginBottom: 8 }}
                    >
                        <Radio value={ULD_LOCATION_TYPE.AIRPORT}>机场</Radio>
                        <Radio value={ULD_LOCATION_TYPE.OTHER_LOCATION}>其他地点</Radio>
                    </Radio.Group>
                </Form.Item>

                {locationType === ULD_LOCATION_TYPE.AIRPORT ? (
                    <Form.Item
                        name="airport_iata"
                        label="机场"
                        rules={[{ required: true, message: "请选择机场" }]}
                    >
                        <Select
                            placeholder="请选择机场"
                            showSearch
                            loading={airportsLoading}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.children ?? "").toLowerCase().includes(input.toLowerCase())
                            }
                            notFoundContent={airportsLoading ? <Spin size="small" /> : "暂无数据"}
                        >
                            {(airportsList || []).map((airport) => (
                                <Option key={airport.iata_code} value={airport.iata_code}>
                                    {airport.iata_code}
                                    {airport.name_cn || airport.name_en
                                        ? ` - ${airport.name_cn || airport.name_en}`
                                        : ""}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                ) : (
                    <Form.Item
                        name="other_location"
                        label="其他地点描述"
                        rules={[{ required: true, message: "请填写位置描述" }]}
                        extra="不能是带运输趟次的地点（那属于在途）"
                    >
                        <Input placeholder="例如：维修车间" maxLength={255} />
                    </Form.Item>
                )}

                <Form.Item
                    name="reason"
                    label="找回原因"
                    rules={[{ required: true, whitespace: true, message: "必须填写原因" }]}
                >
                    <TextArea placeholder="请说明在哪里找到了这块板、板况如何" rows={3} showCount maxLength={500} />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default UldRecoverModal;
